import React, { useEffect, useState } from "react";
import { ArrowRight, Clapperboard, Swords, Check, X, CalendarDays } from "lucide-react";
import api from "../lib/api";
import { TV, TvKicker } from "./TriviaTheme";

const Pill = ({ on, onClick, testid, children }) => (
  <button data-testid={testid} onClick={onClick}
    className={`px-2.5 py-1 rounded-full font-ui text-xs font-semibold border transition-[background-color,color] ${on ? "bg-[#161514] text-white border-[#161514]" : "bg-[#EEE8DD] text-[#4A453E] border-[#DDD5C7] hover:bg-[#E6DFD2]"}`}>
    {children}
  </button>
);

const BoxOfficeCard = ({ pack, onPlay }) => {
  const [decade, setDecade] = useState(0);
  const decs = Object.keys(pack.decades).map(Number).filter((d) => d >= 1930).sort();
  const n = decade ? pack.decades[String(decade)] || 0 : pack.total;
  return (
    <div className={`${TV.card} p-5 flex flex-col tv-rise`} data-testid="box-office-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="tv-caps text-[#57524B]">Box Office</p>
          <p className="font-ui font-bold text-lg text-[#161514] mt-2 leading-snug">The 1,000 biggest films of all time</p>
          <p className="font-ui text-[13px] text-[#77716A] mt-1">Which took more, release years, takings, directors and stars.</p>
        </div>
        <span className="w-9 h-9 shrink-0 rounded-full bg-[#EEE8DD] flex items-center justify-center"><Clapperboard className="w-4 h-4 text-[#161514]" /></span>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-4">
        <Pill on={!decade} onClick={() => setDecade(0)} testid="box-office-decade-all">All</Pill>
        {decs.map((d) => <Pill key={d} on={decade === d} onClick={() => setDecade(d)} testid={`box-office-decade-${d}`}>{String(d).slice(2)}s</Pill>)}
      </div>
      <div className="flex items-center justify-between gap-3 mt-auto pt-5">
        <span className="font-ui text-xs text-[#77716A]" data-testid="box-office-count">{n.toLocaleString()} questions</span>
        <button data-testid="play-box-office" onClick={() => onPlay({ pack: "box-office", ...(decade ? { decade: String(decade) } : {}) })}
          className={`${TV.pillDark} inline-flex items-center gap-1.5 px-4 py-2 font-ui text-sm font-semibold`}>Play round <ArrowRight className="w-4 h-4" /></button>
      </div>
    </div>
  );
};

const BattlesCard = ({ pack, onPlay }) => (
  <div className={`${TV.card} p-5 flex flex-col tv-rise`} data-testid="battles-card">
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="tv-caps text-[#57524B]">Battles of History</p>
        <p className="font-ui font-bold text-lg text-[#161514] mt-2 leading-snug">1,000 battles from 1046 BC to the Falklands</p>
        <p className="font-ui text-[13px] text-[#77716A] mt-1">When, where, which war, who fought and how it ended.</p>
      </div>
      <span className="w-9 h-9 shrink-0 rounded-full bg-[#EEE8DD] flex items-center justify-center"><Swords className="w-4 h-4 text-[#161514]" /></span>
    </div>
    <div className="flex items-center justify-between gap-3 mt-auto pt-5">
      <span className="font-ui text-xs text-[#77716A]" data-testid="battles-count">{pack.total.toLocaleString()} questions</span>
      <button data-testid="play-battles" onClick={() => onPlay({ pack: "battles" })}
        className={`${TV.pillDark} inline-flex items-center gap-1.5 px-4 py-2 font-ui text-sm font-semibold`}>Play round <ArrowRight className="w-4 h-4" /></button>
    </div>
  </div>
);

const BattleToday = () => {
  const [d, setD] = useState(null);
  const [res, setRes] = useState(null);
  const [pick, setPick] = useState(null);
  useEffect(() => { api.get("/trivia/battle-today").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return null;
  const q = d.question;
  const answer = async (o) => {
    if (res) return;
    setPick(o);
    try { setRes((await api.post("/trivia/check", { id: q.id, answer: o })).data); } catch { setPick(null); }
  };
  const cls = (o) => !res ? "bg-[#F7F4EE] border-[#E4DED2] hover:bg-[#EEE8DD] text-[#161514]"
    : o === res.correct_answer ? "bg-[#2D4A3E] border-[#2D4A3E] text-white"
    : o === pick ? "bg-[#F5E4DF] border-[#E7C3B9] text-[#7E3628]" : "bg-[#F7F4EE] border-[#ECE6DB] text-[#A9A399]";
  const today = new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long" });
  return (
    <div className={`${TV.card} p-5 sm:p-6 mt-3 tv-rise`} data-testid="battle-today">
      <p className="tv-caps text-[#57524B] flex items-center gap-2"><CalendarDays className="w-3.5 h-3.5" /> On this day in battle · {today}</p>
      <p className="font-ui font-bold text-lg sm:text-xl text-[#161514] mt-2" data-testid="battle-today-name">{d.battle}</p>
      <p className="font-ui text-xs text-[#77716A] mt-0.5">{d.battle_date} · {d.era} · {d.region}</p>
      <p className="font-ui font-semibold text-[15px] text-[#161514] mt-4" data-testid="battle-today-question">{q.question}</p>
      <div className="grid sm:grid-cols-2 gap-2 mt-3">
        {q.options.map((o, i) => (
          <button key={o} data-testid={`battle-today-option-${i}`} onClick={() => answer(o)} disabled={!!res}
            className={`text-left rounded-[14px] border px-4 py-3 font-ui text-sm font-semibold transition-[background-color,color] ${cls(o)}`}>{o}</button>
        ))}
      </div>
      {res && (
        <div className={`${TV.inset} p-4 mt-4`} data-testid="battle-today-result">
          <p className={`font-ui text-sm font-semibold flex items-center gap-1.5 ${res.correct ? "text-[#2D4A3E]" : "text-[#9E4535]"}`}>
            {res.correct ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />} {res.correct ? "Correct!" : `It was ${res.correct_answer}.`}
          </p>
          <p className="font-ui text-sm text-[#4A453E] mt-1">{d.summary}</p>
        </div>
      )}
    </div>
  );
};

export const DatasetRounds = ({ onPlay }) => {
  const [packs, setPacks] = useState(null);
  useEffect(() => { api.get("/trivia/dataset-packs").then((r) => setPacks(r.data)).catch(() => setPacks({})); }, []);
  if (!packs) return null;
  return (
    <div className="mt-10" data-testid="dataset-rounds">
      <TvKicker line={false}>Special rounds</TvKicker>
      <h2 className="font-ui font-bold text-xl sm:text-2xl text-[#161514] mt-2 mb-4">Films &amp; battles</h2>
      <div className="grid sm:grid-cols-2 gap-3">
        {packs["box-office"] && <BoxOfficeCard pack={packs["box-office"]} onPlay={onPlay} />}
        {packs.battles && <BattlesCard pack={packs.battles} onPlay={onPlay} />}
      </div>
      <BattleToday />
    </div>
  );
};
